import type { OrgArtifactViewDefinitionLike } from '../models/orgArtifactKind';
import { isFullOrgLiveView } from '../models/orgArtifactKind';
import { presentAtlasPlanningError } from './atlasPlanningErrorPresentation';

type AuthenticatedFetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface ClientScenarioPreparationIssue {
  readonly code: string;
  readonly severity?: 'error' | 'warning';
  readonly message: string;
  readonly recordIds?: readonly string[];
}

interface PreparationErrorPayload {
  readonly error?: string;
  readonly code?: string;
  readonly retryable?: boolean;
  readonly blockedReason?: string;
  readonly blockedDetail?: string;
  readonly issues?: readonly ClientScenarioPreparationIssue[];
}

export class ClientScenarioPreparationError extends Error {
  constructor(
    message: string,
    readonly outcome: 'not_prepared' | 'unknown',
    readonly details: {
      readonly code?: string;
      readonly status?: number;
      readonly retryable?: boolean;
      readonly blockedReason?: string;
      readonly blockedDetail?: string;
      readonly issues?: readonly ClientScenarioPreparationIssue[];
    } = {},
  ) {
    super(message);
    this.name = 'ClientScenarioPreparationError';
  }
}

/**
 * A client scenario is operational only when it belongs to the canonical Full
 * Org Chart. Planning charts must leave their complete head staged for the
 * dashboard review flow. The server re-verifies the exact artifact before it
 * creates an operational draft; this client decision only selects the existing
 * completion interaction.
 */
export function clientScenarioCompletion(
  viewDefinition: OrgArtifactViewDefinitionLike | null,
): 'prepare_operational' | 'stage' {
  const isUnfilteredFullOrgChart = isFullOrgLiveView(viewDefinition)
    && !viewDefinition?.filters?.departmentKey;
  return isUnfilteredFullOrgChart ? 'prepare_operational' : 'stage';
}

export async function prepareClientScenarioOperationalChange(input: {
  readonly authenticatedFetch: AuthenticatedFetch;
  readonly folderId: string;
  readonly diagramId: string;
  readonly changeSetId: string;
}): Promise<string> {
  const { authenticatedFetch, folderId, diagramId, changeSetId } = input;
  const base = `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${changeSetId}`;
  const reviewResponse = await authenticatedFetch(`${base}/review`, { cache: 'no-store' }).catch(() => {
    const presentation = presentAtlasPlanningError({ status: 503, retryable: true }, { operation: 'review', draftSaved: true });
    throw new ClientScenarioPreparationError(presentation.message, 'not_prepared', { code: 'review_unavailable', status: 503, retryable: true });
  });
  const review = await reviewResponse.json().catch(() => null) as {
    diff?: readonly { externalId?: unknown }[];
    error?: string;
    code?: string;
    issues?: readonly ClientScenarioPreparationIssue[];
  } | null;
  if (!reviewResponse.ok) {
    const presentation = presentAtlasPlanningError({
      status: reviewResponse.status,
      code: review?.code ?? review?.error,
      message: review?.error,
    }, { operation: 'review', draftSaved: true });
    throw new ClientScenarioPreparationError(presentation.message, 'not_prepared', {
      code: review?.code ?? review?.error,
      status: reviewResponse.status,
    });
  }
  const factExternalIds = [...new Set(
    (Array.isArray(review?.diff) ? review.diff : []).flatMap((entry) => (
      typeof entry?.externalId === 'string' && entry.externalId.trim()
        ? [entry.externalId.trim()]
        : []
    )),
  )];
  if (factExternalIds.length === 0) {
    const presentation = presentAtlasPlanningError({ code: 'invalid_change_set' }, { operation: 'prepare', draftSaved: true });
    throw new ClientScenarioPreparationError(presentation.message, 'not_prepared', { code: 'invalid_change_set' });
  }

  const response = await authenticatedFetch(`${base}/prepare-operational`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ masterDiagramId: diagramId, factExternalIds }),
  }).catch(() => {
    const presentation = presentAtlasPlanningError({ outcome: 'unknown' }, { operation: 'prepare', draftSaved: true });
    throw new ClientScenarioPreparationError(presentation.message, 'unknown', { code: 'preparation_outcome_unknown' });
  });
  const payload = await response.json().catch(() => null) as ({ id?: string } & PreparationErrorPayload) | null;
  if (!response.ok || typeof payload?.id !== 'string' || !payload.id.trim()) {
    // A timeout, server failure, in-progress response or malformed success is
    // not evidence that the deterministic transfer did not commit.
    const rejected = !response.ok && [400, 401, 403, 404, 409, 422].includes(response.status)
      && typeof payload?.error === 'string' && payload.error !== 'intent_in_progress';
    throw new ClientScenarioPreparationError(
      presentAtlasPlanningError({
        status: response.status,
        code: payload?.code ?? payload?.error,
        message: payload?.error,
        retryable: payload?.retryable,
        blockedReason: payload?.blockedReason,
        blockedDetail: payload?.blockedDetail,
        issues: payload?.issues,
      }, { operation: 'prepare', draftSaved: true }).message,
      rejected ? 'not_prepared' : 'unknown',
      {
        ...(payload?.code || payload?.error ? { code: payload.code ?? payload.error } : {}),
        status: response.status,
        ...(payload?.retryable !== undefined ? { retryable: payload.retryable } : {}),
        ...(payload?.blockedReason ? { blockedReason: payload.blockedReason } : {}),
        ...(payload?.blockedDetail ? { blockedDetail: payload.blockedDetail } : {}),
        ...(Array.isArray(payload?.issues) ? { issues: payload.issues } : {}),
      },
    );
  }
  return payload.id;
}
